import React from "react";
import Button from "../../../ui/Button";
import Modal from "../../../ui/Modal";
import { colors, radius, spacing, typography } from "../../../ui/tokens";
import RiskPrintPreview from "./RiskPrintPreview";
import { PRINT_FIELD_DEFINITIONS, PRINT_SUB_OPTION_DEFINITIONS } from "./riskPrintConfig";

const checkboxGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: spacing.sm,
};

const checkboxItemStyle = {
  display: "flex",
  alignItems: "flex-start",
  gap: spacing.sm,
  border: `1px solid ${colors.border}`,
  borderRadius: radius.sm,
  padding: spacing.md,
  background: colors.surfaceSubtle,
};

const warningStyle = {
  border: `1px solid ${colors.warningBorder}`,
  background: colors.warningSubtle,
  color: colors.warning,
  borderRadius: radius.sm,
  padding: spacing.md,
  fontSize: typography.size.sm,
};

const helperPanelStyle = {
  display: "grid",
  gap: spacing.md,
};

export default function RiskPrintModal({
  open,
  selectedKeys,
  subOptions,
  onToggleField,
  onToggleSubOption,
  onCancel,
  onPreview,
  onPrint,
  printing = false,
  previewVisible,
  preview,
  overflow,
}) {
  return (
    <Modal
      open={open}
      onClose={onCancel}
      title="Print risk to one page"
      description="Select the details to include in the printable PDF. The output will be formatted to fit on a single page."
      size="xl"
      footer={(
        <>
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="secondary" onClick={onPreview}>
            Preview PDF
          </Button>
          <Button variant="primary" onClick={onPrint} disabled={printing}>
            {printing ? "Downloading..." : "Download PDF"}
          </Button>
        </>
      )}
    >
      <div style={helperPanelStyle}>
        {overflow?.likelyOverflow && (
          <div role="alert" style={warningStyle}>
            Selected content is likely to exceed one page. Consider removing long narrative fields or linked lists.
          </div>
        )}

        <div style={checkboxGridStyle}>
          {PRINT_FIELD_DEFINITIONS.map((field) => {
            const checked = selectedKeys.includes(field.key);
            const childOptions = PRINT_SUB_OPTION_DEFINITIONS.filter((option) => option.parentKey === field.key);
            return (
              <div key={field.key} style={{ display: "grid", gap: spacing.xs }}>
                <label style={checkboxItemStyle}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => onToggleField(field.key)}
                  />
                  <span style={{ fontSize: typography.size.sm, color: colors.text }}>{field.label}</span>
                </label>
                {childOptions.length > 0 && (
                  <div style={{ display: "grid", gap: spacing.xs, paddingLeft: spacing.xl }}>
                    {childOptions.map((option) => (
                      <label
                        key={option.key}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: spacing.sm,
                          fontSize: typography.size.sm,
                          color: checked ? colors.text : colors.textMuted,
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={Boolean(subOptions?.[option.key])}
                          disabled={!checked}
                          onChange={() => onToggleSubOption(option.key)}
                        />
                        <span>{option.label}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {previewVisible && preview && (
          <RiskPrintPreview
            preview={preview}
            screen
            testId="risk-print-preview-screen"
          />
        )}
      </div>
    </Modal>
  );
}
